import { useState, useEffect } from "react";
import { ENDPOINTS } from "../config/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { SUBSCRIPTION_PLANS } from "../data/mockData";
import Modal from "../components/ui/Modal";
import {
  Building2,
  Download,
  ArrowRight,
  RefreshCw,
  Store,
  LogOut,
  Sun,
  Moon,
  Mail,
  Phone,
  Check,
  X,
  Zap,
  Crown,
  Sparkles,
  ChevronLeft,
  ShoppingBag,
  BadgeCheck,
  FileText,
  CreditCard,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────────────

function daysRemaining(endDate) {
  const end = new Date(endDate);
  const now = new Date();
  return Math.floor((end - now) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-NG", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(amount);
}

// ── Plan style maps ──────────────────────────────────────────────────────────

const PLAN_ACCENT = {
  gray: {
    header: "from-gray-800/60 to-gray-900/60",
    btn: "bg-gray-700 hover:bg-gray-600 text-gray-200",
    selectedRing: "ring-2 ring-gray-500",
    check: "text-gray-400",
  },
  violet: {
    header: "from-violet-900/50 to-indigo-900/40",
    btn: "bg-violet-600 hover:bg-violet-500 text-white",
    selectedRing: "ring-2 ring-violet-500",
    check: "text-violet-400",
  },
  amber: {
    header: "from-amber-900/30 to-orange-900/20",
    btn: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white",
    selectedRing: "ring-2 ring-amber-500",
    check: "text-amber-400",
  },
};

const PLAN_ICONS = {
  free: <Zap size={18} className="text-gray-400" />,
  advanced: <Sparkles size={18} className="text-violet-400" />,
  premium: <Crown size={18} className="text-amber-400" />,
};

// ── Main component ───────────────────────────────────────────────────────────

export default function TenantProperty() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [marketplaceStep, setMarketplaceStep] = useState(null); // null | 'plans' | 'payment' | 'form'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    shopName: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tenant profile data
  useEffect(() => {
    const fetchTenantData = async () => {
      if (!user?.phoneNumber || !user?.token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          ENDPOINTS.tenantProfile(user.phoneNumber),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user.token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg =
            errorData?.message ||
            errorData?.error ||
            response.statusText ||
            "Failed to fetch tenant data";
          throw new Error(errorMsg);
        }

        const data = await response.json();
        setTenantData(data);
      } catch (err) {
        console.error("Error fetching tenant data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [user?.phoneNumber, user?.token]);

  // Use fetched tenancies or empty array
  const properties = tenantData?.tenancies || [];

  // Check if user already has marketplace access (from initial data or after registration)
  const hasMarketplaceAccount =
    user?.hasMarketplaceAccount ||
    localStorage.getItem(`mkt_marketplace_${user?.id}`) === "true";

  const handleMarketplaceClick = () => {
    if (hasMarketplaceAccount) {
      navigate("/dashboard");
    } else {
      setMarketplaceStep("plans");
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    if (plan.price === 0) {
      setMarketplaceStep("form");
    } else {
      setMarketplaceStep("payment");
    }
  };

  const handlePaymentConfirm = () => {
    setMarketplaceStep("form");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      localStorage.setItem(`mkt_marketplace_${user?.id}`, "true");
      navigate("/dashboard");
    }, 900);
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const closePlans = () => setMarketplaceStep(null);
  const closePayment = () => {
    setMarketplaceStep(null);
    setSelectedPlan(null);
  };
  const closeForm = () => {
    setMarketplaceStep(null);
    setSelectedPlan(null);
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDark ? "bg-gray-950 text-gray-100" : "bg-gray-50 text-gray-900"
      }`}>
      {/* ── Navbar ── */}
      <header
        className={`sticky top-0 z-30 backdrop-blur-md border-b transition-colors duration-300 ${
          isDark
            ? "bg-gray-900/90 border-gray-800"
            : "bg-white/95 border-gray-200 shadow-sm"
        }`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          {/* Logo + Title */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-glow-violet flex-shrink-0">
              <Store size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">Providha</p>
              <p
                className={`text-xs truncate font-medium ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}>
                Tenant Property Management
              </p>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Desktop CTA */}
            <button
              onClick={handleMarketplaceClick}
              className="hidden sm:flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors active:scale-95">
              <ShoppingBag size={13} />
              View &amp; Manage My Products
              <ArrowRight size={13} />
            </button>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className={`p-2 rounded-xl transition-colors ${
                isDark
                  ? "text-gray-400 hover:bg-gray-800"
                  : "text-gray-500 hover:bg-gray-100"
              }`}>
              {isDark ? (
                <Sun size={18} className="text-amber-400" />
              ) : (
                <Moon size={18} className="text-indigo-400" />
              )}
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              title="Sign out"
              className={`p-2 rounded-xl transition-colors ${
                isDark
                  ? "text-gray-400 hover:text-rose-400 hover:bg-rose-500/10"
                  : "text-gray-500 hover:text-rose-500 hover:bg-rose-50"
              }`}>
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Main content ── */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Tenant Profile Header */}
        <TenantProfileHeader
          user={user}
          tenantData={tenantData}
          isDark={isDark}
        />

        {/* Section heading + desktop CTA row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2
              className={`text-lg font-bold ${
                isDark ? "text-gray-100" : "text-gray-900"
              }`}>
              My Leasing
            </h2>
            <p
              className={`text-sm mt-0.5 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}>
              {loading ? "Loading..." : properties.length}{" "}
              {properties.length === 1 ? "active lease" : "active leases"} found
            </p>
          </div>
        </div>

        {/* Mobile CTA */}
        <button
          onClick={handleMarketplaceClick}
          className="sm:hidden w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold px-4 py-3 rounded-xl transition-colors active:scale-95">
          <ShoppingBag size={15} />
          View &amp; Manage My Products
          <ArrowRight size={15} />
        </button>

        {/* Error state */}
        {error && (
          <div
            className={`rounded-2xl border p-5 ${
              isDark
                ? "bg-rose-500/10 border-rose-500/20"
                : "bg-rose-50 border-rose-200"
            }`}>
            <p className={isDark ? "text-rose-300" : "text-rose-700"}>
              {error}
            </p>
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div
            className={`rounded-2xl border p-12 text-center ${
              isDark
                ? "bg-gray-900 border-gray-800"
                : "bg-white border-gray-200"
            }`}>
            <div className="flex items-center justify-center mb-4">
              <svg
                className="animate-spin w-8 h-8 text-violet-600"
                viewBox="0 0 24 24"
                fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="30 70"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <p
              className={`text-sm ${
                isDark ? "text-gray-500" : "text-gray-400"
              }`}>
              Loading your tenant profile...
            </p>
          </div>
        ) : properties.length === 0 ? (
          <div
            className={`rounded-2xl border p-12 text-center ${
              isDark
                ? "bg-gray-900 border-gray-800"
                : "bg-white border-gray-200"
            }`}>
            <Building2
              size={40}
              className={`mx-auto mb-4 ${
                isDark ? "text-gray-700" : "text-gray-300"
              }`}
            />
            <p
              className={`text-sm ${
                isDark ? "text-gray-500" : "text-gray-400"
              }`}>
              No active property leases found.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {properties.map((prop, index) => (
              <PropertyCard
                key={prop.id}
                prop={prop}
                isDark={isDark}
                index={index}
              />
            ))}
          </div>
        )}
      </main>

      {/* ── Plan Selection Modal ── */}
      <Modal
        isOpen={marketplaceStep === "plans"}
        onClose={closePlans}
        title="Choose Your Marketplace Plan"
        size="xl">
        <PlanSelectionContent isDark={isDark} onSelect={handlePlanSelect} />
      </Modal>

      {/* ── Payment Screen Modal ── */}
      <Modal
        isOpen={marketplaceStep === "payment"}
        onClose={closePayment}
        title={`Payment · ${selectedPlan?.name ?? ""} Plan`}
        size="md">
        <PaymentScreenContent
          isDark={isDark}
          selectedPlan={selectedPlan}
          onConfirm={handlePaymentConfirm}
          onBack={() => setMarketplaceStep("plans")}
        />
      </Modal>

      {/* ── Registration Form Modal ── */}
      <Modal
        isOpen={marketplaceStep === "form"}
        onClose={closeForm}
        title={`Marketplace Registration · ${selectedPlan?.name ?? ""}`}
        size="lg">
        <RegistrationFormContent
          isDark={isDark}
          selectedPlan={selectedPlan}
          formData={formData}
          onChange={handleFormChange}
          onSubmit={handleFormSubmit}
          onBack={() =>
            selectedPlan?.price === 0
              ? setMarketplaceStep("plans")
              : setMarketplaceStep("payment")
          }
          submitting={submitting}
        />
      </Modal>
    </div>
  );
}

// ── Tenant Profile Header ────────────────────────────────────────────────────

function TenantProfileHeader({ user, tenantData, isDark }) {
  if (!user) return null;

  const gradientColors = [
    "from-violet-500 via-indigo-500 to-purple-600",
    "from-amber-500 via-orange-500 to-rose-500",
    "from-emerald-500 via-teal-500 to-cyan-500",
  ];
  // Pick gradient deterministically by user id
  const bgGradient =
    gradientColors[(user.id?.charCodeAt(0) || 0) % gradientColors.length];

  // Prefer the fetched tenant profile; fall back to what was stored at login time
  const firstName =
    tenantData?.firstName || user.firstName || user.name?.split(" ")[0] || "T";
  const lastName =
    tenantData?.lastName || user.lastName || user.name?.split(" ")[1] || "P";
  const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();

  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-colors duration-300 ${
        isDark
          ? "bg-gray-900 border-gray-800"
          : "bg-white border-gray-200 shadow-sm"
      }`}>
      {/* Profile body */}
      <div className="px-5 sm:px-7 py-6">
        {/* Avatar */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="flex items-end gap-4">
            {/* Square avatar with rounded corners */}
            <div
              className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br ${bgGradient} flex items-center justify-center text-white text-2xl sm:text-3xl font-black border-4 shadow-lg flex-shrink-0 ${
                isDark ? "border-gray-900" : "border-white"
              }`}>
              {initials}
            </div>

            {/* Name + business (visible on sm+) */}
            <div className="hidden sm:block pb-1">
              <div className="flex items-center gap-2">
                <h1
                  className={`text-xl font-black bg-gradient-to-r ${bgGradient} bg-clip-text text-transparent`}>
                  {firstName} {lastName}
                </h1>
                {user.verified && (
                  <BadgeCheck
                    size={18}
                    className="text-violet-400 flex-shrink-0"
                  />
                )}
              </div>
              <p
                className={`text-sm font-medium mt-0.5 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}>
                Tenant Account
              </p>
            </div>
          </div>

          {/* Plan badge */}
          <div className="sm:pb-1">
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${
                user.plan === "Premium"
                  ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                  : user.plan === "Advanced"
                    ? "bg-violet-500/15 text-violet-400 border-violet-500/30"
                    : "bg-gray-500/15 text-gray-400 border-gray-500/30"
              }`}>
              {user.plan === "Premium" && <Crown size={11} />}
              {user.plan === "Advanced" && <Sparkles size={11} />}
              {user.plan || "Tenant"} Account
            </span>
          </div>
        </div>

        {/* Name row – mobile only */}
        <div className="mt-3 sm:hidden">
          <div className="flex items-center gap-2">
            <h1
              className={`text-lg font-black bg-gradient-to-r ${bgGradient} bg-clip-text text-transparent`}>
              {firstName} {lastName}
            </h1>
            {user.verified && (
              <BadgeCheck size={16} className="text-violet-400 flex-shrink-0" />
            )}
          </div>
          <p
            className={`text-sm font-medium mt-0.5 ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}>
            Tenant Account
          </p>
        </div>

        {/* Contact info */}
        <div
          className={`mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm ${
            isDark ? "text-gray-400" : "text-gray-500"
          }`}>
          {user.email && (
            <span className="flex items-center gap-1.5">
              <Mail size={14} className="text-violet-400 flex-shrink-0" />
              {user.email}
            </span>
          )}
          {user.phone && (
            <span className="flex items-center gap-1.5">
              <Phone size={14} className="text-violet-400 flex-shrink-0" />
              {user.phone}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Property Card ────────────────────────────────────────────────────────────

function PropertyCard({ prop, isDark, index }) {
  const days = daysRemaining(prop.rentEndDate);

  // Determine status based on payment status and days remaining
  let status = "active";
  if (days < 0) {
    status = "expired";
  } else if (days < 30 && prop.paymentSummary?.paymentRequired) {
    status = "due_soon";
  }

  const statusConfig = {
    active: {
      label: "Active",
      color: "text-emerald-400",
      bg: isDark ? "bg-emerald-500/10" : "bg-emerald-50",
      border: "border-emerald-500/20",
      dot: "bg-emerald-400",
    },
    due_soon: {
      label: "Due Soon",
      color: "text-amber-400",
      bg: isDark ? "bg-amber-500/10" : "bg-amber-50",
      border: "border-amber-500/20",
      dot: "bg-amber-400",
    },
    expired: {
      label: "Expired",
      color: "text-rose-400",
      bg: isDark ? "bg-rose-500/10" : "bg-rose-50",
      border: "border-rose-500/20",
      dot: "bg-rose-400",
    },
  };

  const statusInfo = statusConfig[status] || statusConfig.active;

  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-colors duration-300 ${
        isDark
          ? "bg-gray-900 border-gray-800"
          : "bg-white border-gray-200 shadow-sm"
      }`}>
      {/* Card header */}
      <div
        className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
          isDark
            ? "border-gray-800 bg-gray-800/30"
            : "border-gray-100 bg-gray-50"
        }`}>
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border border-violet-500/20 ${
              isDark ? "bg-violet-500/10" : "bg-violet-50"
            }`}>
            <Building2 size={20} className="text-violet-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-sm">
                {prop.property?.name || "Property"}
              </h2>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  isDark
                    ? "bg-gray-700 text-gray-400"
                    : "bg-gray-200 text-gray-500"
                }`}>
                Property {index + 1}
              </span>
            </div>
            <p
              className={`text-xs mt-0.5 ${
                isDark ? "text-gray-500" : "text-gray-400"
              }`}>
              {prop.property?.locationAddress}, {prop.property?.locationState}
            </p>
          </div>
        </div>
        <span
          className={`self-start sm:self-auto flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${statusInfo.bg} ${statusInfo.border} ${statusInfo.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`} />
          {statusInfo.label}
        </span>
      </div>

      {/* Details grid */}
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <InfoItem
            label="Rent Amount"
            value={formatCurrency(prop.rentAmount)}
            isDark={isDark}
            highlight
          />
          <InfoItem label="Plan" value={prop.paymentPlan} isDark={isDark} />
          <InfoItem
            label="Rent Start"
            value={formatDate(prop.rentStartDate)}
            isDark={isDark}
          />
          <InfoItem
            label="Rent End"
            value={formatDate(prop.rentEndDate)}
            isDark={isDark}
          />
          <InfoItem
            label="Last Payment"
            value={prop.lastPaymentAt ? formatDate(prop.lastPaymentAt) : "N/A"}
            isDark={isDark}
          />
          <DaysRemainingItem days={days} isDark={isDark} />
        </div>

        {/* Payment Summary Section */}
        {prop.paymentSummary && (
          <>
            <div
              className={`border-t ${
                isDark ? "border-gray-800" : "border-gray-100"
              }`}
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <InfoItem
                label="Total Due"
                value={formatCurrency(prop.paymentSummary.totalDue)}
                isDark={isDark}
                highlight={prop.paymentSummary.totalDue > 0}
              />
              <InfoItem
                label="Total Paid"
                value={formatCurrency(prop.paymentSummary.totalPaidAmount)}
                isDark={isDark}
              />
              <InfoItem
                label="Outstanding"
                value={formatCurrency(prop.paymentSummary.outstandingAmount)}
                isDark={isDark}
              />
              <InfoItem
                label="Payment Status"
                value={prop.paymentSummary.paymentRequired ? "Due" : "Paid"}
                isDark={isDark}
              />
            </div>
          </>
        )}

        {/* Divider */}
        <div
          className={`border-t ${
            isDark ? "border-gray-800" : "border-gray-100"
          }`}
        />

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button className="flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors active:scale-95">
            <RefreshCw size={16} />
            Renew Rent
          </button>
          <button
            className={`flex items-center justify-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors border active:scale-95 ${
              isDark
                ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-200"
                : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"
            }`}>
            <FileText size={16} />
            Download Leasing Agreement
          </button>
          <button
            className={`flex items-center justify-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors border active:scale-95 ${
              isDark
                ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-200"
                : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"
            }`}>
            <Download size={16} />
            Download Receipt
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ label, value, isDark, highlight }) {
  return (
    <div
      className={`p-3.5 rounded-xl ${
        isDark ? "bg-gray-800/50" : "bg-gray-50"
      }`}>
      <p
        className={`text-xs mb-1 ${
          isDark ? "text-gray-500" : "text-gray-400"
        }`}>
        {label}
      </p>
      <p
        className={`text-sm font-semibold ${
          highlight
            ? "text-violet-400"
            : isDark
              ? "text-gray-100"
              : "text-gray-900"
        }`}>
        {value}
      </p>
    </div>
  );
}

function DaysRemainingItem({ days, isDark }) {
  const isExpired = days < 0;
  const isUrgent = !isExpired && days < 30;

  return (
    <div
      className={`p-3.5 rounded-xl ${
        isDark ? "bg-gray-800/50" : "bg-gray-50"
      }`}>
      <p
        className={`text-xs mb-1 ${
          isDark ? "text-gray-500" : "text-gray-400"
        }`}>
        Days Remaining
      </p>
      <p
        className={`text-sm font-semibold ${
          isExpired
            ? "text-rose-400"
            : isUrgent
              ? "text-amber-400"
              : isDark
                ? "text-gray-100"
                : "text-gray-900"
        }`}>
        {isExpired ? "Expired" : `${days} days`}
      </p>
    </div>
  );
}

// ── Plan Selection (inside Modal) ────────────────────────────────────────────

function PlanSelectionContent({ isDark, onSelect }) {
  return (
    <div className="space-y-4">
      <p
        className={`text-sm text-center mb-5 ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}>
        Select a plan to access the Providha Marketplace and start selling.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {SUBSCRIPTION_PLANS.map((plan) => {
          const a = PLAN_ACCENT[plan.accentColor] || PLAN_ACCENT.gray;
          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl border overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-lg group ${
                isDark
                  ? "bg-gray-800 border-gray-700 hover:border-gray-500"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => onSelect(plan)}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-violet-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-glow-violet whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Header */}
              <div
                className={`bg-gradient-to-br ${a.header} px-5 pt-5 pb-4 border-b ${
                  isDark ? "border-gray-700" : "border-gray-100"
                }`}>
                <div className="flex items-center gap-2.5 mb-3">
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                      isDark ? "bg-gray-800/60" : "bg-white/70"
                    } border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                    {PLAN_ICONS[plan.id]}
                  </div>
                  <h3
                    className={`text-sm font-bold ${
                      isDark ? "text-gray-100" : "text-gray-900"
                    }`}>
                    {plan.name}
                  </h3>
                </div>
                <div className="flex items-baseline gap-1">
                  {plan.price === 0 ? (
                    <span
                      className={`text-2xl font-black ${
                        isDark ? "text-gray-100" : "text-gray-900"
                      }`}>
                      Free
                    </span>
                  ) : (
                    <>
                      <span
                        className={`text-xs font-semibold ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}>
                        ₦
                      </span>
                      <span
                        className={`text-2xl font-black ${
                          isDark ? "text-gray-100" : "text-gray-900"
                        }`}>
                        {plan.price.toLocaleString()}
                      </span>
                      <span
                        className={`text-xs ${
                          isDark ? "text-gray-500" : "text-gray-400"
                        }`}>
                        {plan.period}
                      </span>
                    </>
                  )}
                </div>
                <p
                  className={`text-xs mt-1 ${
                    isDark ? "text-gray-500" : "text-gray-400"
                  }`}>
                  {plan.description}
                </p>
              </div>

              {/* Features (top 4) */}
              <div className="px-5 py-4 space-y-2">
                {plan.features.slice(0, 4).map((f, i) => (
                  <div key={i} className="flex items-start gap-2">
                    {f.included ? (
                      <Check
                        size={13}
                        className="text-emerald-400 flex-shrink-0 mt-0.5"
                      />
                    ) : (
                      <X
                        size={13}
                        className={`flex-shrink-0 mt-0.5 ${
                          isDark ? "text-gray-600" : "text-gray-300"
                        }`}
                      />
                    )}
                    <span
                      className={`text-xs ${
                        f.included
                          ? isDark
                            ? "text-gray-300"
                            : "text-gray-700"
                          : isDark
                            ? "text-gray-600"
                            : "text-gray-400"
                      }`}>
                      {f.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Select button */}
              <div className="px-5 pb-5">
                <button
                  className={`w-full py-2.5 rounded-xl text-xs font-bold transition-all duration-200 active:scale-95 ${a.btn}`}>
                  Select {plan.name}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Payment Screen (inside Modal) ───────────────────────────────────────────

function PaymentScreenContent({ isDark, selectedPlan, onConfirm, onBack }) {
  const [processing, setProcessing] = useState(false);

  const handleConfirm = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      onConfirm();
    }, 1200);
  };

  return (
    <div className="space-y-6">
      {/* Plan summary */}
      <div
        className={`rounded-2xl border p-5 ${
          isDark
            ? "bg-gray-800/60 border-gray-700"
            : "bg-gray-50 border-gray-200"
        }`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            {selectedPlan?.id === "premium" ? (
              <Crown size={20} className="text-amber-400" />
            ) : (
              <Zap size={20} className="text-violet-400" />
            )}
          </div>
          <div>
            <p
              className={`font-bold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
              {selectedPlan?.name} Plan
            </p>
            <p
              className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              ₦{selectedPlan?.price?.toLocaleString()}
              {selectedPlan?.period}
            </p>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-2">
          {selectedPlan?.features?.map((feat, i) => (
            <li
              key={i}
              className={`flex items-center gap-2 text-sm ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}>
              <Check size={14} className="text-violet-400 flex-shrink-0" />
              {feat.text ?? feat}
            </li>
          ))}
        </ul>
      </div>

      {/* Payment summary */}
      <div
        className={`rounded-2xl border p-5 ${
          isDark
            ? "bg-gray-800/60 border-gray-700"
            : "bg-gray-50 border-gray-200"
        }`}>
        <p
          className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
            isDark ? "text-gray-500" : "text-gray-400"
          }`}>
          Payment Summary
        </p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className={isDark ? "text-gray-400" : "text-gray-500"}>
              {selectedPlan?.name} Plan
            </span>
            <span
              className={`font-semibold ${isDark ? "text-gray-100" : "text-gray-900"}`}>
              ₦{selectedPlan?.price?.toLocaleString()}
            </span>
          </div>
          <div
            className={`border-t pt-2 flex justify-between text-sm font-bold ${
              isDark
                ? "border-gray-700 text-gray-100"
                : "border-gray-200 text-gray-900"
            }`}>
            <span>Total</span>
            <span>₦{selectedPlan?.price?.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
            isDark
              ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300"
              : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"
          }`}>
          <ChevronLeft size={15} />
          Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={processing}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 ${
            processing
              ? "bg-violet-600/50 text-white/60 cursor-not-allowed"
              : "bg-violet-600 hover:bg-violet-500 text-white"
          }`}>
          {processing ? (
            <>
              <svg
                className="animate-spin w-4 h-4"
                viewBox="0 0 24 24"
                fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="30 70"
                  strokeLinecap="round"
                />
              </svg>
              Processing Payment…
            </>
          ) : (
            <>
              <CreditCard size={15} />
              Confirm Payment
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Registration Form (inside Modal) ────────────────────────────────────────

function RegistrationFormContent({
  isDark,
  selectedPlan,
  formData,
  onChange,
  onSubmit,
  onBack,
  submitting,
}) {
  const inputCls = `w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-violet-500/50 ${
    isDark
      ? "bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-600"
      : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white"
  }`;

  const labelCls = `block text-xs font-semibold mb-1.5 ${
    isDark ? "text-gray-400" : "text-gray-600"
  }`;

  const isValid = formData.shopName.trim() && formData.phone.trim();

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Selected plan reminder */}
      {selectedPlan && (
        <div
          className={`flex items-center gap-3 p-3 rounded-xl border text-sm ${
            isDark
              ? "bg-violet-500/10 border-violet-500/20 text-violet-300"
              : "bg-violet-50 border-violet-200 text-violet-700"
          }`}>
          <div className="w-7 h-7 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
            {PLAN_ICONS[selectedPlan.id]}
          </div>
          <div>
            <span className="font-bold">{selectedPlan.name} Plan</span>
            <span
              className={`ml-2 text-xs ${
                isDark ? "text-violet-400" : "text-violet-500"
              }`}>
              {selectedPlan.price === 0
                ? "Free"
                : `₦${selectedPlan.price.toLocaleString()}${selectedPlan.period}`}
            </span>
          </div>
          <button
            type="button"
            onClick={onBack}
            className={`ml-auto text-xs font-semibold flex items-center gap-1 ${
              isDark
                ? "text-violet-400 hover:text-violet-300"
                : "text-violet-600 hover:text-violet-700"
            }`}>
            <ChevronLeft size={12} />
            Change
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Market Name – auto-populated */}
        <div>
          <label className={labelCls}>Market Name</label>
          <input
            type="text"
            value="Computer Village"
            readOnly
            className={`${inputCls} opacity-70 cursor-not-allowed`}
          />
        </div>

        {/* Market Address – auto-populated */}
        <div>
          <label className={labelCls}>Market Address</label>
          <input
            type="text"
            value="Ikeja, Lagos"
            readOnly
            className={`${inputCls} opacity-70 cursor-not-allowed`}
          />
        </div>

        {/* Shop Name */}
        <div>
          <label className={labelCls}>Shop Name</label>
          <input
            type="text"
            name="shopName"
            value={formData.shopName}
            onChange={onChange}
            placeholder="e.g. GadgetWorld"
            className={inputCls}
            required
          />
        </div>

        {/* Phone Number */}
        <div>
          <label className={labelCls}>Phone Number</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={onChange}
            placeholder="+234 800 000 0000"
            className={inputCls}
            required
          />
        </div>
      </div>

      {/* Submit */}
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
            isDark
              ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300"
              : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"
          }`}>
          <ChevronLeft size={15} />
          Back
        </button>
        <button
          type="submit"
          disabled={!isValid || submitting}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 ${
            !isValid || submitting
              ? "bg-violet-600/50 text-white/60 cursor-not-allowed"
              : "bg-violet-600 hover:bg-violet-500 text-white"
          }`}>
          {submitting ? (
            <>
              <svg
                className="animate-spin w-4 h-4"
                viewBox="0 0 24 24"
                fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="30 70"
                  strokeLinecap="round"
                />
              </svg>
              Setting up your marketplace…
            </>
          ) : (
            <>
              <ShoppingBag size={15} />
              Enter Marketplace
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
